import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Blade from '@/components/ui/Blade/Blade';
import { ManagementContactClassificationAdditionalInfoDtoIn } from '@/models/in/ManagementContactClassificationAdditionalInfoDtoIn';
import { ContactClassificationDtoIn } from '@/models/in/SupportedDtoIn';
import { ContactClassificationStatus } from '@/models/ContactClassificationStatus';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import userSupport from '@/assets/icons/user-support.svg';
import ClassificationCard from './ClassificationCard';
import StatusChangeDialog from './StatusChangeDialog';
import RequestCompletionDialog from '@/components/RequestCompletionDialog/RequestCompletionDialog';
import OfficeCommentsDialog from '../OfficeCommentsDialog/OfficeCommentsDialog';
import styles from './AdditionalInfo.module.css';

interface AdditionalInfoProps {
  contactId: number;
  fundId: number;
  contactName: string;
  israeliId: string;
  handleXclick: () => void;
  onRefresh: () => void;
}

const AdditionalInfo = ({
  contactId,
  fundId,
  contactName,
  israeliId,
  handleXclick,
  onRefresh,
}: AdditionalInfoProps) => {
  const { t } = useTranslation();
  const [selectedClassification, setSelectedClassification] = useState<ContactClassificationDtoIn | null>(null);
  const [selectedNewStatus, setSelectedNewStatus] = useState<ContactClassificationStatus | null>(null);
  const [isOfficeCommentsDialogOpen, setIsOfficeCommentsDialogOpen] = useState(false);
  const [selectedClassificationForCommentsDialog, setSelectedClassificationForCommentsDialog] = useState<ContactClassificationDtoIn | null>(null);
  const [requestCompletionClassification, setRequestCompletionClassification] = useState<ContactClassificationDtoIn | null>(null);

  // Fetch full classification details for this contact/fund pair
  const { data: additionalInfo, isLoading, refetch: refetchAdditionalInfo } = useGetAuthApi<ManagementContactClassificationAdditionalInfoDtoIn>(
    `management/classifications/${contactId}/funds/${fundId}/additional-info`
  );

  // Sync selectedClassificationForCommentsDialog with updated additionalInfo data
  useEffect(() => {
    if (selectedClassificationForCommentsDialog && additionalInfo) {
      const allClassifications = [
        ...(additionalInfo.activeClassifications || []),
        ...(additionalInfo.pendingClassifications || []),
        ...(additionalInfo.inactiveClassifications || []),
      ];
      const updatedClassification = allClassifications.find(
        (c) => c.id === selectedClassificationForCommentsDialog.id
      );
      if (updatedClassification) {
        setSelectedClassificationForCommentsDialog(updatedClassification);
      }
    }
  }, [additionalInfo, selectedClassificationForCommentsDialog?.id]);

  const handleOpenStatusDialog = useCallback((classification: ContactClassificationDtoIn, newStatus: ContactClassificationStatus) => {
    setSelectedClassification(classification);
    setSelectedNewStatus(newStatus);
  }, []);

  const handleCloseStatusDialog = useCallback(() => {
    setSelectedClassification(null);
    setSelectedNewStatus(null);
  }, []);

  const handleStatusChangeSuccess = useCallback(() => {
    handleCloseStatusDialog();
    onRefresh(); // Refresh the table
    // Close the blade: after a status change the record may no longer match the
    // active status filter, so leaving it open would show details of a row that
    // is no longer in the table.
    handleXclick();
  }, [handleCloseStatusDialog, onRefresh, handleXclick]);

  const handleOpenOfficeCommentsDialog = useCallback((classification: ContactClassificationDtoIn) => {
    setSelectedClassificationForCommentsDialog(classification);
    setIsOfficeCommentsDialogOpen(true);
  }, []);

  const handleCloseOfficeCommentsDialog = useCallback(() => {
    setIsOfficeCommentsDialogOpen(false);
    setSelectedClassificationForCommentsDialog(null);
  }, []);

  const handleOfficeCommentsSuccess = useCallback(() => {
    refetchAdditionalInfo();
    onRefresh();
  }, [refetchAdditionalInfo, onRefresh]);

  const handleOpenRequestCompletion = useCallback((classification: ContactClassificationDtoIn) => {
    setRequestCompletionClassification(classification);
  }, []);

  const handleCloseRequestCompletion = useCallback(() => {
    setRequestCompletionClassification(null);
  }, []);

  const handleRequestCompletionSuccess = useCallback(() => {
    onRefresh();
    // Requesting completion changes the status too, so close the blade as well.
    handleXclick();
  }, [onRefresh, handleXclick]);

  const activeClassifications = additionalInfo?.activeClassifications || [];
  const pendingClassifications = additionalInfo?.pendingClassifications || [];
  const inactiveClassifications = additionalInfo?.inactiveClassifications || [];
  const allClassifications = [...activeClassifications, ...pendingClassifications, ...inactiveClassifications];

  const headerTop = (
    <div className="flex items-center gap-3">
      <img src={userSupport} alt="" className="w-6 h-6" />
      <div className="flex flex-col">
        <span className="body-lr-semibold text-(--font-body-4)">
          {contactName}
        </span>
        <span className="body-sm-regular text-(--font-body-4)">
          {israeliId}
        </span>
      </div>
    </div>
  );

  const headerBottom = (
    <div className="flex items-center w-full justify-between">
      <div className="flex gap-2">
        <span className="body-md-medium text-(--font-body-4)">
          {t('fundId')}: {fundId}
        </span>
      </div>
    </div>
  );

  return (
    <>
      <Blade handleXclick={handleXclick} headerTop={headerTop} headerBottom={headerBottom} width="600px">
        <div className={`${styles.content} custom-scrollbar`}>
          {/* Loading State */}
          {isLoading && (
            <div className={styles.loadingState}>
              <p className="body-md-regular text-(--font-body-2)">
                {t('loading')}...
              </p>
            </div>
          )}

          {/* Content when loaded */}
          {!isLoading && (
            <>
              {/* Pending Classifications Section */}
              {pendingClassifications.length > 0 && (
                <div className={styles.section}>
                  <h3 className="body-lr-semibold text-(--font-sub-title) mb-3">
                    {t('pendingClassifications')} ({pendingClassifications.length})
                  </h3>
                  <div className={styles.classificationsGrid}>
                    {pendingClassifications.map((classification) => (
                      <ClassificationCard
                        key={classification.id}
                        classification={classification}
                        onStatusChange={(newStatus) => handleOpenStatusDialog(classification, newStatus)}
                        onOpenOfficeComments={() => handleOpenOfficeCommentsDialog(classification)}
                        onRequestCompletion={() => handleOpenRequestCompletion(classification)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Active Classifications Section */}
              {activeClassifications.length > 0 && (
                <div className={styles.section}>
                  <h3 className="body-lr-semibold text-(--font-sub-title) mb-3">
                    {t('activeClassifications')} ({activeClassifications.length})
                  </h3>
                  <div className={styles.classificationsGrid}>
                    {activeClassifications.map((classification) => (
                      <ClassificationCard
                        key={classification.id}
                        classification={classification}
                        onStatusChange={(newStatus) => handleOpenStatusDialog(classification, newStatus)}
                        onOpenOfficeComments={() => handleOpenOfficeCommentsDialog(classification)}
                        onRequestCompletion={() => handleOpenRequestCompletion(classification)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Inactive (Rejected/Cancelled) Classifications Section */}
              {inactiveClassifications.length > 0 && (
                <div className={styles.section}>
                  <h3 className="body-lr-semibold text-(--font-sub-title) mb-3">
                    {t('inactiveClassifications')} ({inactiveClassifications.length})
                  </h3>
                  <div className={styles.classificationsGrid}>
                    {inactiveClassifications.map((classification) => (
                      <ClassificationCard
                        key={classification.id}
                        classification={classification}
                        onStatusChange={(newStatus) => handleOpenStatusDialog(classification, newStatus)}
                        onOpenOfficeComments={() => handleOpenOfficeCommentsDialog(classification)}
                        onRequestCompletion={() => handleOpenRequestCompletion(classification)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* No Classifications */}
              {allClassifications.length === 0 && (
                <div className={styles.emptyState}>
                  <p className="body-md-regular text-(--font-body-2)">
                    {t('noClassifications')}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </Blade>

      {/* Status Change Dialog */}
      {selectedClassification && selectedNewStatus && (
        <StatusChangeDialog
          open={true}
          onClose={handleCloseStatusDialog}
          classificationId={selectedClassification.id}
          newStatus={selectedNewStatus}
          onSuccess={handleStatusChangeSuccess}
        />
      )}

      {/* Office Comments Dialog */}
      {selectedClassificationForCommentsDialog && (
        <OfficeCommentsDialog
          open={isOfficeCommentsDialogOpen}
          onClose={handleCloseOfficeCommentsDialog}
          contactClassificationId={selectedClassificationForCommentsDialog.id}
          statusHistory={selectedClassificationForCommentsDialog.statusHistory || []}
          officeComments={selectedClassificationForCommentsDialog.officeComments || []}
          onSuccess={handleOfficeCommentsSuccess}
        />
      )}

      {/* Request Completion Dialog */}
      {requestCompletionClassification && (
        <RequestCompletionDialog
          isOpen={true}
          onClose={handleCloseRequestCompletion}
          apiEndpoint={`management/classifications/${requestCompletionClassification.id}/request-completion`}
          onSuccess={handleRequestCompletionSuccess}
          documents={requestCompletionClassification.documents || []}
          showVisibilityToggle={false}
        />
      )}
    </>
  );
};

export default AdditionalInfo;

